// © 2025 Camp Promethean, LLC. All Rights Reserved.

import { supabase } from "@/integrations/supabase/client";

export const TOOLTIPS_CHANGED_EVENT = "cp:tooltips-changed";

function storageKey(userId: string): string {
  return `cp:tooltips:${userId}`;
}

/** Read tooltip preference from local storage (defaults to true). */
export function readTooltipsEnabled(userId: string): boolean {
  try {
    const stored = localStorage.getItem(storageKey(userId));
    if (stored === null) return true;
    return stored === "true";
  } catch {
    return true;
  }
}

/** Persist locally and notify listeners immediately. */
export function writeTooltipsEnabled(userId: string, enabled: boolean): void {
  localStorage.setItem(storageKey(userId), String(enabled));
  window.dispatchEvent(
    new CustomEvent(TOOLTIPS_CHANGED_EVENT, { detail: { userId, enabled } }),
  );
}

/**
 * Save tooltip preference. Always writes to localStorage; syncs to profiles when
 * the tooltips_enabled column exists (no-op if migration not yet applied).
 */
export async function persistTooltipsEnabled(
  userId: string,
  enabled: boolean,
): Promise<void> {
  writeTooltipsEnabled(userId, enabled);

  const { error } = await supabase
    .from("profiles")
    .update({ tooltips_enabled: enabled })
    .eq("id", userId);

  // 42703 = undefined_column — migration not applied yet; localStorage is sufficient.
  if (error && error.code !== "42703") {
    throw error;
  }
}
